import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { HealthBadge } from "@/components/HealthBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, ArrowRight, Copy, Trash, CheckSquare, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_DOT = {
  planning: "bg-slate-400", active: "bg-emerald-600", on_hold: "bg-amber-500", completed: "bg-slate-900",
};
const empty = { name: "", type: "residential", location: "", client_name: "", status: "planning", start_date: "", notes: "", square_feet: 0 };

export default function Projects() {
  const { user } = useAuth();
  const isManager = ["owner", "admin"].includes(user?.role);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [dupSource, setDupSource] = useState(null);
  const [dupName, setDupName] = useState("");
  const [dupItems, setDupItems] = useState(true);
  const [dupActuals, setDupActuals] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/projects").then((r) => setProjects(r.data));
  useEffect(() => { load(); }, []);

  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSelected = projects.length > 0 && selected.length === projects.length;
  const toggleAll = () => setSelected(allSelected ? [] : projects.map((p) => p.id));
  const exitSelect = () => { setSelectMode(false); setSelected([]); };

  const askDeleteSingle = (e, p) => { e.stopPropagation(); setPendingDelete([p]); };
  const askDeleteSelected = () => {
    const list = projects.filter((p) => selected.includes(p.id));
    if (list.length) setPendingDelete(list);
  };

  const confirmDelete = async () => {
    if (!pendingDelete?.length) return;
    setDeleting(true);
    try {
      const ids = pendingDelete.map((p) => p.id);
      const { data } = await api.post("/projects/bulk-delete", { ids });
      toast.success(`${data.deleted} project${data.deleted === 1 ? "" : "s"} moved to trash`);
      setPendingDelete(null);
      exitSelect();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setDeleting(false); }
  };

  const openDuplicate = (e, p) => {
    e.stopPropagation();
    setDupSource(p);
    setDupName(`${p.name} (Copy)`);
    setDupItems(true);
    setDupActuals(false);
  };

  const doDuplicate = async () => {
    if (!dupName.trim()) { toast.error("Enter a name for the new project"); return; }
    setDuplicating(true);
    try {
      const { data } = await api.post(`/projects/${dupSource.id}/duplicate`, {
        name: dupName.trim(), include_line_items: dupItems, include_actuals: dupActuals,
      });
      toast.success(`Project duplicated${data.line_items_copied ? ` — ${data.line_items_copied} line items copied` : ""}`);
      setDupSource(null);
      navigate(`/projects/${data.project.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setDuplicating(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/projects", form);
      toast.success("Project created with default phases");
      setOpen(false);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader label="Portfolio" title="Projects">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-project-button" className="rounded-none"><Plus size={16} className="mr-1" /> New Project</Button>
          </DialogTrigger>
          <DialogContent className="rounded-none">
            <DialogHeader><DialogTitle className="font-heading">New Project</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="project-form">
              <div>
                <Label className="label-caps">Project Name</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="project-name-input" className="mt-2" placeholder="Maple St. Residence" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="label-caps">Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger className="mt-2 rounded-none" data-testid="project-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="residential">Residential</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-caps">Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="mt-2 rounded-none" data-testid="project-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="planning">Planning</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="on_hold">On Hold</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="label-caps">Client</Label>
                  <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                    className="mt-2" data-testid="project-client-input" />
                </div>
                <div>
                  <Label className="label-caps">Location</Label>
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                    className="mt-2" data-testid="project-location-input" />
                </div>
              </div>
              <div>
                <Label className="label-caps">Building Size (sq ft)</Label>
                <Input type="number" value={form.square_feet} onChange={(e) => setForm({ ...form, square_feet: e.target.value })}
                  className="mt-2 font-mono-fig" data-testid="project-sqft-input" placeholder="e.g. 2400" />
              </div>
              <div>
                <Label className="label-caps">Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2" />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={saving} data-testid="project-submit-button" className="rounded-none">
                  {saving ? "Creating…" : "Create Project"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="p-4 sm:p-8">
        {projects.length === 0 ? (
          <div className="border border-dashed border-border p-16 text-center">
            <p className="font-heading text-xl font-bold mb-2">No projects yet</p>
            <p className="text-muted-foreground text-sm">Create your first build to start tracking costs.</p>
          </div>
        ) : (
          <>
          {isManager && (
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap" data-testid="projects-toolbar">
              {selectMode ? (
                <>
                  <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} className="h-4 w-4 accent-black" data-testid="select-all-projects" />
                    Select all ({selected.length}/{projects.length})
                  </label>
                  <div className="flex items-center gap-2">
                    <Button variant="destructive" disabled={!selected.length} onClick={askDeleteSelected} className="rounded-none" data-testid="delete-selected-button">
                      <Trash size={16} className="mr-1" /> Delete ({selected.length})
                    </Button>
                    <Button variant="outline" onClick={exitSelect} className="rounded-none" data-testid="cancel-select-button">Cancel</Button>
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-4">
                  <button onClick={() => setSelectMode(true)} data-testid="select-projects-button"
                    className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">
                    <CheckSquare size={16} /> Select to delete
                  </button>
                  <button onClick={() => navigate("/trash")} data-testid="view-trash-button"
                    className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">
                    <Trash size={16} /> Trash
                  </button>
                </div>
              )}
            </div>
          )}
          {/* Mobile cards */}
          <div className="md:hidden space-y-3 stagger">
            {projects.map((p) => (
              <div key={p.id} role="button" tabIndex={0} data-testid={`project-card-${p.id}`}
                onClick={() => (selectMode ? toggleSelect(p.id) : navigate(`/projects/${p.id}`))}
                className={`w-full text-left border p-4 bg-white hover-lift cursor-pointer ${selectMode && selected.includes(p.id) ? "border-primary ring-1 ring-primary" : "border-border"}`}>
                <div className="flex items-center gap-2 mb-3">
                  {selectMode && (
                    <input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggleSelect(p.id)} onClick={(e) => e.stopPropagation()}
                      className="h-4 w-4 accent-black shrink-0" data-testid={`select-project-mobile-${p.id}`} />
                  )}
                  <span className={`w-2 h-2 rounded-full ${STATUS_DOT[p.status]}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{p.name}</div>
                    <div className="text-xs text-muted-foreground capitalize">{p.type} · {p.client_name || p.location || "—"}</div>
                    <HealthBadge health={p.health} testId={`project-health-mobile-${p.id}`} className="mt-1.5" />
                  </div>
                  {!selectMode && (
                    <>
                      <button onClick={(e) => openDuplicate(e, p)} data-testid={`duplicate-project-mobile-${p.id}`}
                        title="Duplicate" className="text-muted-foreground hover:text-primary shrink-0 p-1">
                        <Copy size={16} />
                      </button>
                      {isManager && (
                        <button onClick={(e) => askDeleteSingle(e, p)} data-testid={`delete-project-mobile-${p.id}`}
                          title="Delete" className="text-muted-foreground hover:text-destructive shrink-0 p-1">
                          <Trash size={16} />
                        </button>
                      )}
                      <ArrowRight size={16} className="text-muted-foreground shrink-0" />
                    </>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div><div className="label-caps">Budget</div><div className="font-mono-fig">{money(p.total_budget)}</div></div>
                  <div><div className="label-caps">Actual</div><div className="font-mono-fig">{money(p.total_actual)}</div></div>
                  <div><div className="label-caps">Variance</div><div className={`font-mono-fig ${p.variance < 0 ? "text-destructive" : "text-emerald-700"}`}>{p.variance < 0 ? "-" : ""}{money(Math.abs(p.variance))}</div></div>
                </div>
              </div>
            ))}
          </div>
          {/* Desktop table */}
          <div className="border border-border hidden md:block reveal">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  {selectMode && <TableHead className="w-10"></TableHead>}
                  <TableHead>Project</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Health</TableHead>
                  <TableHead className="text-right">Budget</TableHead>
                  <TableHead className="text-right">Actual</TableHead>
                  <TableHead className="text-right">Variance</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {projects.map((p) => (
                  <TableRow key={p.id} data-testid={`project-row-${p.id}`}
                    onClick={() => (selectMode ? toggleSelect(p.id) : navigate(`/projects/${p.id}`))}
                    className={`cursor-pointer hover:bg-secondary transition-colors duration-200 ${selectMode && selected.includes(p.id) ? "bg-secondary" : ""}`}>
                    {selectMode && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggleSelect(p.id)}
                          className="h-4 w-4 accent-black" data-testid={`select-project-${p.id}`} />
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${STATUS_DOT[p.status]}`} />
                        <div>
                          <div className="font-medium">{p.name}</div>
                          <div className="text-xs text-muted-foreground">{p.client_name || p.location || "—"}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="capitalize text-sm text-muted-foreground">{p.type}</TableCell>
                    <TableCell><HealthBadge health={p.health} testId={`project-health-${p.id}`} /></TableCell>
                    <TableCell className="text-right font-mono-fig tabular-nums">{money(p.total_budget)}</TableCell>
                    <TableCell className="text-right font-mono-fig tabular-nums">{money(p.total_actual)}</TableCell>
                    <TableCell className={`text-right font-mono-fig tabular-nums ${p.variance < 0 ? "text-destructive" : "text-emerald-700"}`}>
                      {p.variance < 0 ? "-" : ""}{money(Math.abs(p.variance))}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {!selectMode && (
                        <>
                          <button onClick={(e) => openDuplicate(e, p)} data-testid={`duplicate-project-${p.id}`}
                            title="Duplicate as template" className="text-muted-foreground hover:text-primary mr-3 align-middle">
                            <Copy size={16} className="inline" />
                          </button>
                          {isManager && (
                            <button onClick={(e) => askDeleteSingle(e, p)} data-testid={`delete-project-${p.id}`}
                              title="Delete project" className="text-muted-foreground hover:text-destructive mr-3 align-middle">
                              <Trash size={16} className="inline" />
                            </button>
                          )}
                          <ArrowRight size={16} className="inline text-muted-foreground align-middle" />
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          </>
        )}
      </div>

      {/* Duplicate dialog */}
      <Dialog open={!!dupSource} onOpenChange={(o) => !o && setDupSource(null)}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle className="font-heading">Duplicate as Template</DialogTitle></DialogHeader>
          {dupSource && (
            <div className="space-y-4" data-testid="duplicate-dialog">
              <div className="border border-border p-4 bg-secondary">
                <p className="label-caps mb-1">Source Project</p>
                <div className="font-medium">{dupSource.name}</div>
                <div className="text-sm text-muted-foreground">Copies phases & building details. Status resets to Planning.</div>
              </div>
              <div>
                <Label className="label-caps">New Project Name</Label>
                <Input value={dupName} onChange={(e) => setDupName(e.target.value)} data-testid="duplicate-name-input" className="mt-2" />
              </div>
              <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="duplicate-items-toggle">
                <input type="checkbox" checked={dupItems} onChange={(e) => setDupItems(e.target.checked)} className="h-4 w-4 accent-black" />
                <span className="text-sm">Copy budgeted line items</span>
              </label>
              <label className={`flex items-center gap-3 select-none ${dupItems ? "cursor-pointer" : "opacity-40 cursor-not-allowed"}`} data-testid="duplicate-actuals-toggle">
                <input type="checkbox" disabled={!dupItems} checked={dupActuals} onChange={(e) => setDupActuals(e.target.checked)} className="h-4 w-4 accent-black" />
                <span className="text-sm">Also copy actual costs & statuses <span className="text-muted-foreground">(off = fresh budget template)</span></span>
              </label>
              <DialogFooter>
                <Button onClick={doDuplicate} disabled={duplicating} data-testid="duplicate-submit-button" className="rounded-none">
                  <Copy size={16} className="mr-1" />
                  {duplicating ? "Duplicating…" : "Create Copy"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Delete confirmation */}
      <Dialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <DialogContent className="rounded-none" data-testid="delete-confirm-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Warning size={20} weight="fill" className="text-destructive" /> Delete {pendingDelete?.length === 1 ? "project" : `${pendingDelete?.length} projects`}?</DialogTitle></DialogHeader>
          {pendingDelete && (
            <div className="space-y-4">
              <DialogDescription className="text-sm text-muted-foreground">
                {pendingDelete.length === 1 ? "This project" : "These projects"} will be moved to <strong>Trash</strong>. You can restore {pendingDelete.length === 1 ? "it" : "them"} within 30 days — after that {pendingDelete.length === 1 ? "it's" : "they're"} permanently deleted.
              </DialogDescription>
              <div className="border border-border max-h-48 overflow-y-auto">
                {pendingDelete.map((p) => (
                  <div key={p.id} className="px-3 py-2 border-b border-border last:border-b-0 text-sm flex items-center justify-between gap-2">
                    <span className="font-medium truncate">{p.name}</span>
                    <span className="text-xs text-muted-foreground shrink-0">{money(p.total_budget)}</span>
                  </div>
                ))}
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setPendingDelete(null)} className="rounded-none" data-testid="delete-cancel-button">Cancel</Button>
                <Button variant="destructive" onClick={confirmDelete} disabled={deleting} className="rounded-none" data-testid="delete-confirm-button">
                  <Trash size={16} className="mr-1" />
                  {deleting ? "Moving…" : "Move to Trash"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
